import { customElement, html, css, cssManager, property, state, DeesElement } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { demoFunc } from './dees-storage-browser.demo.js';
import type { IStorageDataProvider, IStorageChangeEvent } from './interfaces.js';
import './dees-storage-columns.js';
import './dees-storage-keys.js';
import './dees-storage-preview.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-storage-browser': DeesStorageBrowser;
  }
}

type TViewType = 'columns' | 'keys';

@customElement('dees-storage-browser')
export class DeesStorageBrowser extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View'];

  @property({ type: Object })
  public accessor dataProvider: IStorageDataProvider | null = null;

  @property({ type: String })
  public accessor bucketName: string = '';

  /**
   * Optional change stream subscription.
   * Pass a function that takes a callback and returns an unsubscribe function.
   */
  @property({ type: Object })
  public accessor onChangeEvent: ((callback: (event: IStorageChangeEvent) => void) => (() => void)) | null = null;

  @state()
  private accessor viewType: TViewType = 'columns';

  @state()
  private accessor currentPrefix: string = '';

  @state()
  private accessor selectedKey: string = '';

  @state()
  private accessor refreshKey: number = 0;

  @state()
  private accessor previewWidth: number = 700;

  @state()
  private accessor isResizingPreview: boolean = false;

  @state()
  private accessor recentChangeCount: number = 0;

  @state()
  private accessor isStreamConnected: boolean = false;

  private changeUnsubscribe: (() => void) | null = null;

  public static styles = [
    cssManager.defaultStyles,
    themeDefaultStyles,
    css`
      :host {
        display: block;
        height: 100%;
      }

      .browser-container {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .toolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: var(--dees-color-bg-tertiary);
        border-radius: 8px;
        margin-bottom: 16px;
      }

      .breadcrumb {
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 1;
        font-size: 14px;
        color: var(--dees-color-text-muted);
      }

      .breadcrumb-item {
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background 0.15s;
      }

      .breadcrumb-item:hover {
        background: var(--dees-color-code-bg);
        color: var(--dees-color-tooltip-bg);
      }

      .breadcrumb-separator {
        color: var(--dees-color-border-strong);
      }

      .view-toggle {
        display: flex;
        gap: 4px;
      }

      .view-btn {
        padding: 6px 12px;
        background: transparent;
        border: 1px solid var(--dees-color-border-strong);
        color: var(--dees-color-text-muted);
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
        transition: all 0.15s;
      }

      .view-btn:hover {
        border-color: var(--dees-color-text-muted);
        color: var(--dees-color-text-secondary);
      }

      .view-btn.active {
        background: var(--dees-color-code-bg);
        border-color: var(--dees-color-text-muted);
        color: var(--dees-color-text-primary);
      }

      .content {
        flex: 1;
        display: grid;
        grid-template-columns: 1fr;
        gap: 0;
        overflow: hidden;
      }

      .content.has-preview {
        grid-template-columns: 1fr 4px var(--preview-width, 700px);
      }

      .resize-divider {
        width: 4px;
        background: transparent;
        cursor: col-resize;
        transition: background 0.2s;
      }

      .resize-divider:hover,
      .resize-divider.active {
        background: var(--dees-color-scrollbar-thumb);
      }

      .main-view {
        overflow: auto;
        background: var(--dees-color-bg-tertiary);
        border-radius: 8px;
      }

      .preview-panel {
        background: var(--dees-color-bg-tertiary);
        border-radius: 8px;
        overflow: hidden;
        margin-left: 12px;
      }

      @media (max-width: 1024px) {
        .content,
        .content.has-preview {
          grid-template-columns: 1fr;
        }

        .preview-panel,
        .resize-divider {
          display: none;
        }
      }

      .stream-status {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: var(--dees-color-text-muted);
        margin-left: auto;
        margin-right: 12px;
      }

      .stream-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--dees-color-text-muted);
      }

      .stream-dot.connected {
        background: #22c55e;
      }

      .change-indicator {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 8px;
        background: rgba(245, 158, 11, 0.2);
        border-radius: 4px;
        font-size: 11px;
        color: #f59e0b;
        margin-right: 12px;
      }

      .change-indicator.pulse {
        animation: pulse-orange 1s ease-in-out;
      }

      @keyframes pulse-orange {
        0% { background: rgba(245, 158, 11, 0.4); }
        100% { background: rgba(245, 158, 11, 0.2); }
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    this.subscribeToChanges();
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    this.unsubscribeFromChanges();
  }

  /**
   * Public method to trigger a refresh of child components
   */
  public refresh() {
    this.refreshKey++;
  }

  private setViewType(type: TViewType) {
    this.viewType = type;
  }

  private navigateToPrefix(prefix: string) {
    this.currentPrefix = prefix;
    this.selectedKey = '';
  }

  private handleKeySelected(e: CustomEvent) {
    this.selectedKey = e.detail.key;
  }

  private handleNavigate(e: CustomEvent) {
    this.navigateToPrefix(e.detail.prefix);
  }

  private handleObjectDeleted(e: CustomEvent) {
    this.selectedKey = '';
    this.refreshKey++;
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('bucketName')) {
      this.selectedKey = '';
      this.currentPrefix = '';
      this.recentChangeCount = 0;
      this.unsubscribeFromChanges();
      this.subscribeToChanges();
    }
    if (changedProperties.has('onChangeEvent')) {
      this.unsubscribeFromChanges();
      this.subscribeToChanges();
    }
  }

  private subscribeToChanges() {
    if (!this.onChangeEvent) {
      this.isStreamConnected = false;
      return;
    }

    try {
      this.changeUnsubscribe = this.onChangeEvent((event: IStorageChangeEvent) => {
        this.handleChange(event);
      });
      this.isStreamConnected = true;
    } catch (error) {
      console.warn('[StorageBrowser] Failed to subscribe to changes:', error);
      this.isStreamConnected = false;
    }
  }

  private unsubscribeFromChanges() {
    if (this.changeUnsubscribe) {
      this.changeUnsubscribe();
      this.changeUnsubscribe = null;
    }
    this.isStreamConnected = false;
  }

  private handleChange(event: IStorageChangeEvent) {
    this.recentChangeCount++;
    this.refreshKey++;
  }

  private startPreviewResize = (e: MouseEvent) => {
    e.preventDefault();
    this.isResizingPreview = true;
    document.addEventListener('mousemove', this.handlePreviewResize);
    document.addEventListener('mouseup', this.endPreviewResize);
  };

  private handlePreviewResize = (e: MouseEvent) => {
    if (!this.isResizingPreview) return;
    const contentEl = this.shadowRoot?.querySelector('.content');
    if (!contentEl) return;
    const containerRect = contentEl.getBoundingClientRect();
    const newWidth = Math.min(Math.max(containerRect.right - e.clientX, 250), 1000);
    this.previewWidth = newWidth;
  };

  private endPreviewResize = () => {
    this.isResizingPreview = false;
    document.removeEventListener('mousemove', this.handlePreviewResize);
    document.removeEventListener('mouseup', this.endPreviewResize);
  };

  render() {
    const breadcrumbParts = this.currentPrefix
      ? this.currentPrefix.split('/').filter(Boolean)
      : [];

    return html`
      <div class="browser-container">
        <div class="toolbar">
          <div class="breadcrumb">
            <span
              class="breadcrumb-item"
              @click=${() => this.navigateToPrefix('')}
            >
              ${this.bucketName}
            </span>
            ${breadcrumbParts.map((part, index) => {
              const prefix = breadcrumbParts.slice(0, index + 1).join('/') + '/';
              return html`
                <span class="breadcrumb-separator">/</span>
                <span
                  class="breadcrumb-item"
                  @click=${() => this.navigateToPrefix(prefix)}
                >
                  ${part}
                </span>
              `;
            })}
          </div>

          ${this.onChangeEvent ? html`
            <div class="stream-status">
              <span class="stream-dot ${this.isStreamConnected ? 'connected' : ''}"></span>
              ${this.isStreamConnected ? 'Live' : 'Offline'}
            </div>
          ` : ''}
          ${this.recentChangeCount > 0
            ? html`
                <div class="change-indicator pulse">
                  ${this.recentChangeCount} change${this.recentChangeCount > 1 ? 's' : ''}
                </div>
              `
            : ''}
          <div class="view-toggle">
            <button
              class="view-btn ${this.viewType === 'columns' ? 'active' : ''}"
              @click=${() => this.setViewType('columns')}
            >
              Columns
            </button>
            <button
              class="view-btn ${this.viewType === 'keys' ? 'active' : ''}"
              @click=${() => this.setViewType('keys')}
            >
              List
            </button>
          </div>
        </div>

        <div class="content ${this.selectedKey ? 'has-preview' : ''}" style="--preview-width: ${this.previewWidth}px">
          <div class="main-view">
            ${this.viewType === 'columns'
              ? html`
                  <dees-storage-columns
                    .dataProvider=${this.dataProvider}
                    .bucketName=${this.bucketName}
                    .currentPrefix=${this.currentPrefix}
                    .refreshKey=${this.refreshKey}
                    @key-selected=${this.handleKeySelected}
                    @navigate=${this.handleNavigate}
                  ></dees-storage-columns>
                `
              : html`
                  <dees-storage-keys
                    .dataProvider=${this.dataProvider}
                    .bucketName=${this.bucketName}
                    .currentPrefix=${this.currentPrefix}
                    .refreshKey=${this.refreshKey}
                    @key-selected=${this.handleKeySelected}
                    @navigate=${this.handleNavigate}
                  ></dees-storage-keys>
                `}
          </div>

          ${this.selectedKey
            ? html`
                <div
                  class="resize-divider ${this.isResizingPreview ? 'active' : ''}"
                  @mousedown=${this.startPreviewResize}
                ></div>
                <div class="preview-panel">
                  <dees-storage-preview
                    .dataProvider=${this.dataProvider}
                    .bucketName=${this.bucketName}
                    .objectKey=${this.selectedKey}
                    @object-deleted=${this.handleObjectDeleted}
                  ></dees-storage-preview>
                </div>
              `
            : ''}
        </div>
      </div>
    `;
  }
}
